import dayjs from "dayjs";
import flow from "lodash/flow";

import { BenchmarkGranularity } from "app/common/benchmark/constants";
import { getMinMaxDate } from "app/common/benchmark/dateRange";

const dateFormats = {
    shortYearMonthPeriod: "YYYY-MM",
    shortYearMonthPoint: "YYYY.MM",
    fullMonthYear: "MMMM YYYY",
    shortMonthYear: "MMM YYYY",
    fullYear: "YYYY",
    fullMonth: "MMMM",
    shortMonth: "MMM",
    day: "DD",
    fullDate: "YYYY.MM.DD",
};

export const getLastWeekDuration = () => {
    const { weeklyTo } = getMinMaxDate();
    const lastDate = weeklyTo.format(dateFormats.fullDate);
    const startDate = weeklyTo.subtract(6, "day").format(dateFormats.fullDate);

    return `${startDate}-${lastDate}`;
};

export const getLastMonthDuration = () => {
    const { monthlyTo } = getMinMaxDate();
    const lastDate = monthlyTo.format(dateFormats.shortYearMonthPoint);

    return `${lastDate}-${lastDate}`;
};

export const getDateBySelectedGranularity = (startDate: string, granularity: BenchmarkGranularity) => {
    const { monthlyTo } = getMinMaxDate();

    const _startDate = dayjs(startDate);

    if (granularity === BenchmarkGranularity.WEEKLY) {
        if (_startDate.isAfter(monthlyTo)) {
            return monthlyTo.format(dateFormats.shortYearMonthPeriod);
        }

        return dayjs(startDate).format(dateFormats.shortYearMonthPeriod);
    }

    return startDate;
};

const formatDate = (date: string) => date.replaceAll(".", "-");
const getArrayFromFormattedDate = (date: string) => date.split("-");

// if we have duration in format "2021.01.01-2021.01.07" we need to do some transformations to '2021-01-01-2021-01-07'
const getStartAndEndDateFromDuration = (duration: string) => {
    if (!duration) {
        return {};
    }

    const formattedDurationArray = flow([formatDate, getArrayFromFormattedDate])(duration);
    const halfLength = formattedDurationArray.length / 2;
    const startDate = [...formattedDurationArray].slice(0, halfLength).join("-");
    const endDate = [...formattedDurationArray].slice(-halfLength).join("-");

    return { startDate, endDate };
};

export const createTimeStringFromDuration = (duration: string): string | undefined => {
    if (!duration) {
        return;
    }

    const { startDate } = getStartAndEndDateFromDuration(duration);

    return dayjs(startDate).format(dateFormats.fullMonthYear);
};

export const createWeeklyTimeString = (language = "en", duration?: string, isShort = false) => {
    const { weeklyTo } = getMinMaxDate();

    let durationTo = weeklyTo;

    if (duration) {
        const { endDate } = getStartAndEndDateFromDuration(duration);

        durationTo = dayjs(endDate);
    }

    const year = durationTo.format(dateFormats.fullYear);
    const startMonth = durationTo
        .locale(language)
        .subtract(6, "day")
        .format(isShort ? dateFormats.shortMonth : dateFormats.fullMonth);
    const lastMonth = durationTo.locale(language).format(isShort ? dateFormats.shortMonth : dateFormats.fullMonth);
    const lastDay = durationTo.format(dateFormats.day);
    const startDay = durationTo.subtract(6, "day").format(dateFormats.day);

    return startMonth !== lastMonth
        ? `${startMonth} ${startDay} - ${lastMonth} ${lastDay}, ${year}`
        : `${startMonth} ${startDay}-${lastDay}, ${year}`;
};

export const createMonthlyTimeString = (language = "en", duration?: string, isShort = false) => {
    const { monthlyTo } = getMinMaxDate();
    let durationTo = monthlyTo;

    if (duration) {
        const { endDate } = getStartAndEndDateFromDuration(duration);
        durationTo = dayjs(endDate);
    }

    return durationTo?.locale(language).format(isShort ? dateFormats.shortMonthYear : dateFormats.fullMonthYear);
};

export const getPeriodDatesFromDuration = (duration: string) => {
    const { startDate, endDate } = getStartAndEndDateFromDuration(duration);

    return {
        yoy: {
            year: dayjs(startDate).format(dateFormats.fullYear),
            toYear: dayjs(startDate).subtract(1, "year").format(dateFormats.fullYear),
            month: dayjs(startDate).format(dateFormats.fullMonth),
        },
        mom: {
            year: dayjs(endDate).format(dateFormats.fullYear),
            toYear: dayjs(endDate).subtract(1, "month").format(dateFormats.fullYear),
            month: dayjs(endDate).format(dateFormats.fullMonth),
            toMonth: dayjs(endDate).subtract(1, "month").format(dateFormats.fullMonth),
        },
        wow: {
            year: dayjs(endDate).format(dateFormats.fullYear),
            toYear: dayjs(startDate).format(dateFormats.fullYear),
            month: dayjs(endDate).format(dateFormats.fullMonth),
            toMonth: dayjs(startDate).format(dateFormats.fullMonth),
            day: dayjs(endDate).format(dateFormats.day),
            toDay: dayjs(startDate).format(dateFormats.day),
        },
    };
};
